import { IconButton, Table } from '@dixa/lib-shared-habla';
import { isResponseWithData } from 'lib-shared-auth';

import { conversationsApi } from 'apis/conversations';
import { BulkActionNotificationParams } from 'apis/notifications';
import { AppLink } from 'components/appLink';

import { HistoricalNotification } from '../../notificationsPage.types';
import { NotificationRow } from '../notificationRow/notificationRow';

import { NotificationBulkActionButtons } from './components';

type BulkActionNotificationProps = {
  notification: HistoricalNotification;
  bulkActionData: BulkActionNotificationParams;
  currentNotificationId: string | null;
  setCurrentNotification: (id: string | null) => void;
};

export const BulkActionNotification = ({
  notification,
  bulkActionData,
  currentNotificationId,
  setCurrentNotification,
}: BulkActionNotificationProps): JSX.Element => {
  const isOpen = currentNotificationId === notification.id;

  const [
    readConversationBulkActionFailedActions,
    { failedActions, isFetching },
  ] = conversationsApi.useLazyReadConversationBulkActionFailedActionsQuery({
    selectFromResult: ({ data, ...rest }) => ({
      ...rest,
      failedActions: data?.data.failed_actions ?? [],
    }),
  });

  const handleToggleOpenNotification = async () => {
    if (currentNotificationId === notification.id) {
      setCurrentNotification(null);
      return;
    }

    try {
      const response = await readConversationBulkActionFailedActions(
        { bulkId: bulkActionData.bulk_id },
        true,
      ).unwrap();

      if (isResponseWithData(response)) {
        setCurrentNotification(notification.id);
      }
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <>
      <NotificationRow
        notification={notification}
        message={bulkActionData.message}
        action={
          <NotificationBulkActionButtons bulkActionData={bulkActionData} />
        }
        inlineEndAction={
          bulkActionData.notification_type === 'BulkActionFailed' ||
          bulkActionData.notification_type === 'BulkActionPartialFailed' ? (
            <IconButton
              variant="tertiary"
              icon={isOpen ? 'angle-down' : 'angle-right'}
              isLoading={isFetching}
              onClick={handleToggleOpenNotification}
            />
          ) : undefined
        }
      />

      {isOpen && (
        <Table.Tr>
          <Table.Td colSpan={4} padding="0">
            <Table framed={false}>
              <Table.Tbody>
                {failedActions.map((failedAction, i) => (
                  <Table.Tr key={i}>
                    <Table.Td colSpan={4}>
                      <AppLink
                        path="/conversation/:csid"
                        params={{
                          path: { csid: `${failedAction.conversation_id}` },
                        }}
                      >
                        #{failedAction.conversation_id}
                      </AppLink>{' '}
                      {failedAction.error_message}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.Td>
        </Table.Tr>
      )}
    </>
  );
};
